import { useQuery } from "@apollo/client";

import { TableHeading } from "@/components/Table";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageCareerPlanBool,
  useCanViewCareerPlanBool,
  useRequireCapability
} from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { Navigate, useNavigate, useParams } from "@/router";
import { GET_CAREER_PLAN_HUB_QUERY } from "../../queries";
import CareerPlanListTable from "../_CareerPlanListTable";
import CareerProfileFocusedChrome from "../_CareerProfileFocusedChrome";
import { CAREER_PLAN_LIST_HEADING_BODY } from "../careerPlanDocument";

export default function CareerPlansListPage() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCareerPlan
  });
  const { pgaId } = useParams("/people/:pgaId/career/career-profile/plans");
  const navigate = useNavigate();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canManageCareerPlan = useCanManageCareerPlanBool();
  const careerTargetsEnabled = flags["pga-career-targets"] === true;
  const careerPlansEnabled = flags["pga-career-plan"] === true;
  const canCreatePlan = careerPlansEnabled && canManageCareerPlan;

  const { data, loading, error } = useQuery(GET_CAREER_PLAN_HUB_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !flagsLoaded || !capabilitiesLoaded || !careerTargetsEnabled || !canViewCareerPlan
  });

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (!careerTargetsEnabled && pgaId) {
    return <Navigate to="/people/:pgaId/career/employment" params={{ pgaId }} replace />;
  }
  if (!pgaId) return null;

  const plans = data?.person?.careerPlans ?? [];

  return (
    <CareerProfileFocusedChrome pgaId={pgaId} activeTab="plans">
      <div data-testid="career-plans-list">
        {error ? (
          <div className="mt-12">Error: {error.message}</div>
        ) : (
          <>
            <TableHeading
              heading="Career Plans"
              body={CAREER_PLAN_LIST_HEADING_BODY}
              topSpacing="default"
              actions={
                canCreatePlan
                  ? [
                      {
                        icon: "add",
                        label: "Create Plan",
                        testId: "career-plans-create",
                        onClick: () =>
                          navigate("/people/:pgaId/career/career-profile/onboarding", { params: { pgaId } })
                      }
                    ]
                  : []
              }
            />
            <CareerPlanListTable plans={plans} pgaId={pgaId} loading={loading} canManage={canCreatePlan} />
          </>
        )}
      </div>
    </CareerProfileFocusedChrome>
  );
}
